/*eslint max-lines-per-function: ["error", 260]*/
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { BiArrowBack } from 'react-icons/bi';

import {
  GetDictionaryDtoFromJSON,
  GetEstimateByMonthDto,
  GetProjectDto,
  GetUmpMediaplanDto,
  GetUmpPositionDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { usePokCore } from '../../../common/hooks/usePokCore';
import FormSkeleton from '../../../app/components/FormSkeleton';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { useTranslation } from '../../../common/hooks/useTranslation';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { hasPositionSpecialistTeams } from '../Estimates/SpecialistTeamPhaseUtils';

import { UmpEstimatesGrid } from './UmpEstimatesGrid';
import { UmpPositionsList } from './UmpPositionsList';
import {
  UmpNewPathPhase,
  UmpNewSpecialistTeam,
} from './UmpParameterMapperForm';

interface UmpImportFormProps {
  estimateByMonth: GetEstimateByMonthDto;
  project: GetProjectDto;
  show?: boolean;
  handleClose: () => void;
  onCompleted: (
    positionsToImport: GetUmpPositionDto[],
    isPurchaseSameAsSales: boolean,
    purchaserId?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const UmpImportForm: React.FC<UmpImportFormProps> = props => {
  const [currentStep, setCurrentStep] = useState<
    'estimates' | 'positions' | 'summary'
  >('estimates');
  const [estimatesFromUmp, setEstimatesFromUmp] = useState<
    GetUmpMediaplanDto[]
  >([]);
  const [positionsFromUmp, setPositionsFromUmp] = useState<GetUmpPositionDto[]>(
    [],
  );
  const [selectedEstimate, setSelectedEstimate] = useState<
    GetUmpMediaplanDto | undefined
  >();
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importSummary, setImportSummary] = useState<string[]>([]);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  const getDataFromUmp = useCallback(() => {
    setIsLoading(true);
    pok.umpImport
      .getEstimatesList(props.estimateByMonth.id)
      .then(data => {
        setEstimatesFromUmp(data);
        setSaveErrors([]);
        setShowSaveErrors(true);
        setIsLoading(false);
      })
      .catch(async errorResponse => {
        const errors = await responseErrors(errorResponse);
        setSaveErrors(errors);
        setShowSaveErrors(true);
        setIsLoading(false);
      });
  }, [pok.umpImport, props.estimateByMonth.id]);

  useEffect(() => {
    if (props.show) {
      getDataFromUmp();
    }
  }, [props.show, getDataFromUmp]);

  const onSelectedEstimate = (key: string) => {
    setIsLoading(true);
    pok.umpImport
      .getPositionsList(props.estimateByMonth.id, key)
      .then(data => {
        setPositionsFromUmp(data);
        setCurrentStep('positions');
        const selected = estimatesFromUmp.find(e => e.uid === key);
        setSelectedEstimate(selected);
        setSaveErrors([]);
        setShowSaveErrors(false);
        setIsLoading(false);
      })
      .catch(async errorResponse => {
        const errors = await responseErrors(errorResponse);
        setSaveErrors(errors);
        setShowSaveErrors(true);
        setIsLoading(false);
      });
  };

  const onBackToEstimates = () => {
    setCurrentStep('estimates');
    setPositionsFromUmp([]);
    setSelectedEstimate(undefined);
    setSaveErrors([]);
    setShowSaveErrors(false);
  };

  const updateParams = (
    uid: string,
    newPathPhaseDict: UmpNewPathPhase | undefined,
    newSpecialistTeam: UmpNewSpecialistTeam | undefined,
  ) => {
    setPositionsFromUmp(prev =>
      prev.map(item => {
        if (item.uid !== uid) {
          return item;
        }
        const errors = { ...item.errors };
        let pathPhaseDict = item.pathPhaseDict;
        if (newPathPhaseDict?.id) {
          pathPhaseDict = item.pathPhaseDict
            ? { ...item.pathPhaseDict }
            : GetDictionaryDtoFromJSON({});
          pathPhaseDict.id = newPathPhaseDict.id;
          pathPhaseDict.value = newPathPhaseDict.value || '';
          pathPhaseDict.valueEn = newPathPhaseDict.valueEn || '';
          delete errors.pathPhaseDict;
        }
        if (newSpecialistTeam?.id) {
          delete errors.specialistTeam;
        }
        const teamFields = hasPositionSpecialistTeams(
          item.positionSpecialistTeams,
        )
          ? {
              specialistTeamId: newSpecialistTeam?.id,
              specialistTeamName: newSpecialistTeam?.name,
            }
          : {
              deliveryTeamId: newSpecialistTeam?.id,
              deliveryTeamName: newSpecialistTeam?.name,
            };
        return {
          ...item,
          pathPhaseDict,
          ...teamFields,
          errors,
        };
      }),
    );
  };

  const getModalTitle = () => {
    if (currentStep === 'positions') {
      return (
        <>
          <Button
            variant="link"
            className="text-decoration-none ps-0 text-dark"
            onClick={onBackToEstimates}
          >
            <BiArrowBack size={24} />
          </Button>
          {t(tk.prLang.estimate)}:{' '}
          <b>
            {selectedEstimate?.name} -{' '}
            {selectedEstimate?.purchaser?.name ||
              t(tk.umpImport.unknownPurchaser)}
          </b>
          <h5 className="mt-2">{t(tk.prLang.umpChoosePosition)}</h5>
        </>
      );
    }
    if (currentStep === 'estimates') {
      return (
        <>
          <span>
            {t(tk.umpImport.umpImportDetails)}{' '}
            <b>
              {momencik(props.estimateByMonth?.date, 'YYYY-MM')}{' '}
              {props.estimateByMonth?.profile?.name}
            </b>
            {')'}
          </span>
          <h5 className="mt-2">{t(tk.prLang.umpChooseProject)}</h5>
        </>
      );
    }
    if (currentStep === 'summary') {
      return <>{t(tk.umpImport.umpImportSummary)}</>;
    }

    return null;
  };

  const onImport = async (keys: string[], isPurchaseSameAsSales: boolean) => {
    setSaveErrors([]);
    setShowSaveErrors(false);
    if (keys.length === 0) {
      setSaveErrors([t(tk.common.noPositionsSelected)]);
      setShowSaveErrors(true);
      return;
    }
    const positionsToImport = positionsFromUmp.filter(p =>
      keys.includes(p.uid),
    );

    setIsLoading(true);
    props
      .onCompleted(
        positionsToImport,
        isPurchaseSameAsSales,
        selectedEstimate?.purchaser?.id,
      )
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setImportSummary(saveStatus.errors);
          setCurrentStep('summary');
          return;
        }
        notifications.saveCompleted();
        props.handleClose();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      dialogClassName="modal-70w"
    >
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <FormSkeleton />
        ) : (
          <>
            {currentStep === 'estimates' && (
              <UmpEstimatesGrid
                data={estimatesFromUmp}
                onRowClick={onSelectedEstimate}
              />
            )}
            {currentStep === 'positions' && (
              <UmpPositionsList
                data={positionsFromUmp}
                onBack={onBackToEstimates}
                onImport={onImport}
                profileSchema={props.estimateByMonth.profile.schema}
                companyId={props.project?.company?.id}
                pathPhaseDictionaries={props.project.projectPathPhases?.map(
                  o => o.pathPhaseDictionary,
                )}
                pathPhaseEnabled={props.project?.company?.pathPhase}
                updateParams={updateParams}
              />
            )}
            {currentStep === 'summary' && (
              <div>
                <span>{importSummary[0]}</span>
                <ul>
                  {importSummary.map((error, index) => {
                    if (index > 0) {
                      return <li key={index}>{error}</li>;
                    }
                  })}
                </ul>
              </div>
            )}
          </>
        )}
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="my-3"
        />
      </Modal.Body>
      <Modal.Footer></Modal.Footer>
    </Modal>
  );
};
